import React, { useState, useEffect, useContext } from "react";
import Dropdown from "../../commonComponents/dropdown";
import Loader from "../../../../../components/commonComponents/Loader/Index";
import AddCircle from "../../../../../assets/customSVG/AddCircle";
import CustomDelete from "../../../../../assets/customSVG/CustomDelete";
import Edit from "../../../../../assets/customSVG/Edit";
import Close from "../../../../../assets/customSVG/Close";
import Tick from "../../../../../assets/customSVG/Tick";
import { AttributeTypes, ProfileAttributeCreate, AttributeScopeCreate, ProfileAttributeUpdate, ProfileAttributeDelete, AttributeScopeUpdate, AttributeScopeDelete } from "../../../api/Api";
import { Input, Tooltip } from "antd";
import NotificationContext from "../../../../../providers/NotificationProvider"
import { showSuccessToast, showErrorToast } from "../../../../../components/commonComponents/gxAntdToast/toastUtils"

const inputStyle = {
  border: '1px solid #e5e7eb',
  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
    borderColor: '#e5e7eb',
    '&::after': {
      content: 'none'
    }
  },
  '&:hover': {
    borderColor: '#e5e7eb',
    '&::after': {
      content: 'none'
    }
  },
  '&:active': {
    outline: 'none',
    '&::after': {
      content: 'none'
    }
  }
};

const ColumnMapping = ({
  mappingFields,
  setMappingFields,
  fileColumns,
  attributeScopes,
  setAttributeScopes,
  onBack,
  onImport,
  continueLoader,
  isLoadingAttributes,
  onAddAttribute,
  brandId,
  contacts, // Add contacts prop
}) => {
  const [editingField, setEditingField] = useState(null);
  const [editedLabel, setEditedLabel] = useState('');

  // Check if required fields are mapped
  const isMobileNumberMapped = mappingFields.some(field => 
    (field.attributeName === 'mobile_number' || field.crmField === 'Mobile number') && 
    field.mappingField && field.mappingField !== 'Ignore'
  );
  
  const isCountryCodeMapped = mappingFields.some(field => 
    (field.attributeName === 'country_code' || field.crmField === 'Country code') && 
    field.mappingField && field.mappingField !== 'Ignore'
  );
  
  const canImport = isMobileNumberMapped && isCountryCodeMapped;
  const [activeAddAttributeScope, setActiveAddAttributeScope] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [newAttribute, setNewAttribute] = useState({
    name: '',
    type: '',
    scopeId: null,
    scopeLabel: '',
    typeDropdownOpen: false
  });
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [showAddScope, setShowAddScope] = useState(false);
  const [newScope, setNewScope] = useState({
    name: ''
  });
  const [editingScope, setEditingScope] = useState(null);
  const [editedScopeName, setEditedScopeName] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // NOTIFICATION(Toast)
  const notification = useContext(NotificationContext);

  // Helper function to validate a single value against a type
  const validateSingleValue = (value, typeName, fieldName = '') => {
    if (value === null || value === '') {
      return true; // No warning for null or empty string
    }

    // Special handling for name fields (first_name, last_name)
    const isNameField = ['first_name', 'last_name'].includes(fieldName);
    
    switch (typeName) {
      case 'string':
        if (isNameField) {
          // For name fields, only accept strings that don't contain only numbers
          return typeof value === 'string' && isNaN(Number(value));
        }
        return typeof value === 'string' || (typeof value === 'number' && isFinite(value));
      case 'number':
        if (typeof value === 'string') {
          const num = parseFloat(value);
          return !isNaN(num) && isFinite(num);
        }
        return typeof value === 'number' && isFinite(value);
      case 'date':
        // Check if it's a valid date string format (YYYY-MM-DD or DD/MM/YYYY)
        const dateRegex = /^(\d{4}-\d{2}-\d{2})|(\d{2}\/\d{2}\/\d{4})$/;
        if (dateRegex.test(value)) {
          const date = new Date(value);
          return !isNaN(date.getTime());
        }
        // Check if it's an Excel serial number
        if ((typeof value === 'string' || typeof value === 'number') && value > 0) {
          const date = new Date((value - 25569) * 86400 * 1000); // Convert Excel date to JS date
          return !isNaN(date.getTime());
        }
        return false;
      case 'boolean':
        return typeof value === 'boolean' || value === 'true' || value === 'false';
      default:
        return true;
    }
  };

  // Validate data type compatibility across multiple rows
  const validateDataType = (attributeType, columnName, fieldName = '') => {
    // Get the type definition from attributeTypes
    const typeDef = attributeTypes.find(type => type.name === attributeType);
    if (!typeDef) return true; // Type not found

    // Get up to first 50 rows of data
    const maxRowsToCheck = 50;
    const rowsToCheck = contacts?.slice(0, maxRowsToCheck) || [];

    if (rowsToCheck.length === 0) {
      return true; // No data available for validation
    }

    // Count valid and invalid values
    let validCount = 0;
    let invalidCount = 0;

    rowsToCheck.forEach(row => {
      const value = row[columnName];
      if (validateSingleValue(value, typeDef.name, fieldName)) {
        validCount++;
      } else {
        invalidCount++;
      }
    });

    // Calculate valid percentage
    const total = validCount + invalidCount;
    const validPercentage = (validCount / total) * 100;

    // If more than 30% of values are invalid, show warning
    const INVALID_THRESHOLD = 30; // percentage
    return validPercentage >= (100 - INVALID_THRESHOLD);
  };

  // Update mapping and validate
  const updateMappingField = (fieldIndex, selectedId) => {
    const updatedFields = [...mappingFields];
    const currentField = updatedFields[fieldIndex];

    // Reset validation error when field is cleared or set to ignore
    if (selectedId === '' || selectedId === 'ignore') {
      setValidationErrors(prev => ({
        ...prev,
        [currentField.attributeId]: ''
      }));
      updatedFields[fieldIndex].mappingField = selectedId === 'ignore' ? 'Ignore' : '';
      updatedFields[fieldIndex].isOpen = false;
      setMappingFields(updatedFields);
      return;
    }

    // Get the attribute type from the current field
    const attributeType = currentField.attributeType || currentField.type;

    // Check data type compatibility
    const isValidType = validateDataType(attributeType, selectedId, currentField.attributeName);

    // Update mapping
    updatedFields[fieldIndex].mappingField = selectedId;
    updatedFields[fieldIndex].isOpen = false;

    // Store the data for future validations
    if (fileColumns[0]?.[selectedId]) {
      updatedFields[fieldIndex].data = fileColumns[0][selectedId];
    }

    // Update validation errors
    setValidationErrors(prev => ({
      ...prev,
      [currentField.attributeId]: !isValidType ? `Warning: Some rows contain invalid data types` : ''
    }));

    setMappingFields(updatedFields);
  };


  // To Fethc Attribute Types
  useEffect(() => {
    if (brandId) {
      fetchAttributeTypes();
    }
  }, [brandId]);

  const fetchAttributeTypes = async () => {
    try {
      setIsLoadingTypes(true);
      const response = await AttributeTypes(brandId);
      if (response?.status === 200 && response?.data?.attribute_types) {
        setAttributeTypes(response.data.attribute_types);
      }
    } catch (error) {
      showErrorToast(notification, "Error fetching attribute types " + error);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleAddAttribute = async (scopeId, scopeLabel) => {
    setSelectedScope({ id: scopeId, label: scopeLabel });
    setNewAttribute({
      ...newAttribute,
      scopeId: scopeId,
      scopeLabel: scopeLabel
    });
    setActiveAddAttributeScope(scopeId);
  };

  // To Create Attribute Function
  const handleCreateAttribute = async () => {
    try {
      if (!newAttribute.name || !newAttribute.type) {
        showErrorToast(notification, 'Attribute name and type are required');
        return;
      }

      const payload = {
        brand_id: brandId,
        name: newAttribute.name,
        attribute_type: newAttribute.type,
        attribute_scope_id: newAttribute.scopeId
      };

      const response = await ProfileAttributeCreate(payload);
      if (response?.status === 200 && response.data) {
        // Get the new attribute data from the response
        const newAttributeData = response.data;
        showSuccessToast(notification, 'Attribute created successfully');

        // Get the scope info
        const scope = attributeScopes.find(s => s.id === newAttribute.scopeId);

        // Extract the attribute ID from the response - check common response structures
        const attributeId = newAttributeData.id || 
                          newAttributeData.profile_attribute_id || 
                          (newAttributeData.data && newAttributeData.data.id) || 
                          (newAttributeData.attribute && newAttributeData.attribute.id);
        
        if (!attributeId) {
          console.error('Failed to extract attribute ID from response:', newAttributeData);
          showErrorToast(notification, 'Failed to create attribute: Invalid response from server');
          return;
        }

        // Create a complete mapping field for the newly created attribute
        const newMappingField = {
          scopeId: newAttribute.scopeId,
          scopeName: scope?.name || '',
          scopeLabel: scope?.label || '',
          scopeDefault: scope?.default || false,
          attributeId: attributeId,
          attributeType: newAttribute.type,
          attributeName: newAttribute.name,
          attributeLabel: newAttribute.name, // Using name as label initially
          attributeDefault: false, // New attributes are not default
          mappingField: 'Ignore',
          isOpen: false
        };
        
        // Add the new mapping field to the existing mappingFields
        const updatedMappingFields = [...mappingFields, newMappingField];
        setMappingFields(updatedMappingFields);
        
        // Refresh the attribute list from the server to keep everything in sync
        await onAddAttribute();
        
        // Close the form after successful creation
        setActiveAddAttributeScope(null);
        setNewAttribute({
          name: '',
          type: '',
          scopeId: null,
          scopeLabel: '',
          typeDropdownOpen: false
        });
      } else {
        // Handle error
        showErrorToast(notification, response?.response?.data?.error || 'Failed to create attribute');
      }
    } catch (error) {
      console.error('Error creating attribute:', error);
      showErrorToast(notification, 'Failed to create attribute');
    }
  };

  // To Close Add Attribute Modal
  const handleAddAttributeModalClose = () => {
    setActiveAddAttributeScope(null);
    setNewAttribute({
      name: '',
      type: '',
      scopeId: null,
      scopeLabel: '',
      typeDropdownOpen: false
    });
  };

  // To Edit Field
  const handleEditField = (field) => {
    if (!field.attributeDefault) {
      setEditingField(field.attributeId);
      setEditedLabel(field.attributeLabel);
    }
  };

  // To Edit Scope
  const handleEditScope = (scope) => {
    if (!scope.default) {
      setEditingScope(scope.id);
      setEditedScopeName(scope.name);
    }
  };

  // To Save Scope Edit
  const handleSaveScopeEdit = async () => {
    try {
      if (!editedScopeName.trim()) {
        showErrorToast(notification, 'Scope name cannot be empty');
        return;
      }

      const payload = {
        id: editingScope,
        name: editedScopeName
      };

      const response = await AttributeScopeUpdate(brandId, payload);
      if (response?.status === 200) {
        // Update the scope name in the UI
        const updatedScopes = attributeScopes.map(scope => 
          scope.id === editingScope ? { ...scope, name: editedScopeName, label: editedScopeName } : scope
        );
        // Update the scope label in mapping fields
        const updatedFields = mappingFields.map(field => 
          field.scopeId === editingScope ? { ...field, scopeLabel: editedScopeName } : field
        );
        
        setAttributeScopes(updatedScopes);
        setMappingFields(updatedFields);
        setEditingScope(null);
        setEditedScopeName('');
        showSuccessToast(notification, 'Scope name updated successfully');
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to update scope name');
      }
    } catch (error) {
      showErrorToast(notification, 'Error updating scope name');
    }
  };

  // To Cancel Scope Edit
  const handleCancelScopeEdit = () => {
    setEditingScope(null);
    setEditedScopeName('');
  };

  // To Delete Field
  const handleDeleteField = async (field) => {
    if (!field.attributeDefault) {
      const payload = {
        id: field.attributeId
      }
      try {
        const response = await ProfileAttributeDelete(brandId, payload);
        if (response?.status === 200) {
          const updatedFields = mappingFields.filter(f => f.attributeId !== field.attributeId);
          setMappingFields(updatedFields);
          showSuccessToast(notification, 'Field deleted successfully');
        } else {
          showErrorToast(notification, 'Failed to delete field');
        }
      } catch (error) {
        showErrorToast(notification, 'Error deleting field');
      }
    }
  };

  // To Delete Scope
  const handleDeleteScope = async (scope) => {
    if (!scope.default) {
      const payload = {
        id: scope.id
      }
      try {
        const response = await AttributeScopeDelete(brandId, payload);
        if (response?.status === 200) {
          // Remove scope from attributeScopes
          const updatedScopes = attributeScopes.filter(s => s.id !== scope.id);
          setAttributeScopes(updatedScopes);
          
          // Remove all fields associated with this scope
          const updatedFields = mappingFields.filter(f => f.scopeId !== scope.id);
          setMappingFields(updatedFields);
          
          showSuccessToast(notification, 'Scope deleted successfully');
        } else {
          showErrorToast(notification, 'Failed to delete scope');
        }
      } catch (error) {
        showErrorToast(notification, 'Error deleting scope');
      }
    }
  };

  // To Cancel Edit
  const handleCancelEdit = () => {
    setEditingField(null);
    setEditedLabel('');
  };

  // To Save Edit attribute label
  const handleSaveEdit = async () => {
    try {
      if (!editedLabel.trim()) {
        showErrorToast(notification, 'Field label cannot be empty');
        return;
      }

      const fieldIndex = mappingFields.findIndex(f => f.attributeId === editingField);
      if (fieldIndex === -1) return;

      const updatedField = {
        ...mappingFields[fieldIndex],
        attributeLabel: editedLabel
      };

      // Call API to update attribute label with all required parameters
      const field = mappingFields.find(f => f.attributeId === editingField);
      const payload = {
        attribute_scope_id: field.scopeId,
        name: editedLabel,
        attribute_type: field.attributeType,
        id: field.attributeId
      };
      
      const response = await ProfileAttributeUpdate(brandId, payload);
      if (response?.status === 200) {
        const updatedFields = [...mappingFields];
        updatedFields[fieldIndex] = updatedField;
        setMappingFields(updatedFields);
        setEditingField(null);
        setEditedLabel('');
        showSuccessToast(notification, 'Attribute name updated successfully');
      } else {
        showErrorToast(notification, response?.response?.data?.error ||'Failed to update Attribute name');
      }
    } catch (error) {
      showErrorToast(notification, 'Error updating Attribute name');
    }
  };

  // To Add Scope
  const handleAddScope = () => {
    setShowAddScope(true);
  };

  // To Close Add Scope Modal
  const handleAddScopeClose = () => {
    setShowAddScope(false);
    setNewScope({
      name: ''
    });
  };

  // To Create Scope
  const handleCreateScope = async () => {
    try {
      if (!newScope.name) {
        console.error('Scope name is required');
        return;
      }

      const payload = {
        brand_id: brandId,
        name: newScope.name
      };

      const response = await AttributeScopeCreate(payload);
      if (response?.status === 200 && response.data) {
        // Get the new scope data from the response
        const newScopeData = response.data;
        showSuccessToast(notification, 'Scope created successfully');
        
        // Refresh the attribute list from the server to get the new scope
        await onAddAttribute();
        
        // Close the form after successful creation
        setShowAddScope(false);
        setNewScope({
          name: ''
        });
      } else {
        // Handle error
        showErrorToast(notification, response?.response?.data?.error || 'Failed to create scope');
      }
    } catch (error) {
      showErrorToast(notification, 'Error creating scope');
    }
  };

  return (
    <div className="w-full h-full pb-5">
      <div className="h-[calc(100%-60px)] overflow-y-auto p-[1vw]">
        {isLoadingAttributes ? (
          <div className="flex justify-center items-center py-10">
            <Loader Width={30} Height={30} />
            <p className="ml-3 text-gray-600">Loading attributes...</p>
          </div>
        ) : attributeScopes.length > 0 ? (
          <div className="mb-[60px]">
            {attributeScopes.map((scope) => (
              <div key={scope.id} className="mb-3">
                <div className="flex items-center gap-2">
                  {editingScope === scope.id ? (
                    <Input
                      value={editedScopeName}
                      onChange={(e) => setEditedScopeName(e.target.value)}
                      className="w-[50%] text-[0.8vw] border-none"
                      size="small"
                      style={inputStyle}
                      suffix={
                        <div className="flex items-center">
                          <button
                            onClick={handleSaveScopeEdit}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <Tick width="0.8vw" height="0.8vw" strokeWidth="3.2" />
                          </button>
                          <button
                            className="p-1 hover:bg-gray-100 rounded"
                            onClick={handleCancelScopeEdit}
                          >
                            <Close
                              stroke="#616874"
                              circle={false}
                              width="1.5vw"
                              height="1.5vw"
                            />
                          </button>
                        </div>
                      }
                    />
                  ) : (
                    <div className="flex flex-row items-center gap-2 mb-3">
                      <h3 className="text-gray-700 font-semibold">{scope.label}</h3>
                      {!scope.default && (
                        <>
                          <button
                            onClick={() => handleEditScope(scope)}
                          >
                            <Edit
                              stroke="#616874"
                              width="1vw"
                              height="1vw"
                            />
                          </button>
                          <button
                            onClick={() => handleDeleteScope(scope)}
                          >
                            <CustomDelete
                              color="#F96056"
                              width="1vw"
                              height="1vw"
                            />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* CRM Fields */}
                  <div className="col-span-1">
                    <h4 className="text-gray-700 font-semibold mb-3 text-center">CRM Fields</h4>
                    {mappingFields
                      .filter(field => field.scopeId === scope.id)
                        .map((field) => {
                        return (
                        <div key={field.attributeId} className="py-[10px] flex flex-row gap-2">
                          {editingField === field.attributeId ? (
                            <Input
                              value={editedLabel}
                              onChange={(e) => setEditedLabel(e.target.value)}
                              className="w-[70%] text-[0.8vw] rounded-lg"
                              size="small"
                              style={inputStyle}
                              suffix={
                                <div className="flex items-center">
                                  <button
                                    onClick={handleSaveEdit}
                                    className="p-1 hover:bg-gray-100 rounded"
                                  >
                                    <Tick width="0.8vw" height="0.8vw" strokeWidth="3.2" />
                                  </button>
                                  <button
                                    className="p-1 hover:bg-gray-100 rounded"
                                    onClick={handleCancelEdit}
                                  >
                                    <Close
                                      stroke="#616874"
                                      circle={false}
                                      width="1.5vw"
                                      height="1.5vw"
                                    />
                                  </button>
                                </div>
                              }
                            />
                          ) : (
                            <div className="py-1 px-2 border border-gray-200 rounded-lg w-[70%]">
                              <p className="text-gray-700 text-[0.8vw]">{field.attributeLabel}</p>
                            </div>
                          )}
                          {!field.attributeDefault && (
                            <>
                              <button
                                onClick={() => handleEditField(field)}
                              >
                                <Edit
                                  stroke="#616874"
                                  width="1vw"
                                  height="1vw"
                                />
                              </button>
                              <button
                                onClick={() => handleDeleteField(field)}
                              >
                                <CustomDelete
                                  color="#F96056"
                                  width="1vw"
                                  height="1vw"
                                />
                              </button>
                            </>
                          )}
                        </div>
                      )})}
                  </div>
                  
                  {/* Mapping field */}
                  <div className="col-span-1">
                    <h4 className="text-gray-700 font-semibold mb-3 text-center">Mapping field</h4>
                    {mappingFields
                      .filter(field => field.scopeId === scope.id)
                      .map((field) => {
                        const fieldIndex = mappingFields.findIndex(f => 
                          f.attributeId === field.attributeId && f.scopeId === field.scopeId
                        );
                        
                        return (
                          <div key={field.attributeId} className="py-[10px]">
                            <Dropdown
                              height="max-h-[35vh]"
                              open={field.isOpen}
                              setOpen={(isOpen) => {
                                const updatedFields = [...mappingFields];
                                updatedFields[fieldIndex].isOpen = isOpen;
                                setMappingFields(updatedFields);
                              }}
                              data={[
                                ...fileColumns.map(column => ({
                                  id: column,
                                  label: column,
                                  selected: mappingFields.some(f => f.mappingField === column &&
                                    (f.attributeId !== field.attributeId || f.scopeId !== field.scopeId))
                                })),
                                { id: 'ignore', label: 'Ignore' }
                              ]}
                              editFilter={(selectedId) => {
                                // Check if this column is already mapped to another field
                                const isAlreadyMapped = mappingFields.some(
                                  f => f.mappingField === selectedId &&
                                  (f.attributeId !== field.attributeId || f.scopeId !== field.scopeId) &&
                                  selectedId !== 'ignore'
                                );
                                
                                // Only update if the column is not already mapped or if it's 'Ignore'
                                if (!isAlreadyMapped || selectedId === 'ignore') {
                                  updateMappingField(fieldIndex, selectedId);
                                }
                              }}
                              button={
                                <div className="w-full">
                                  <div
                                    className="flex justify-between items-center py-1 px-2 cursor-pointer transition-colors duration-100 border border-gray-200 rounded-lg"
                                    onClick={() => {
                                      const updatedFields = [...mappingFields];
                                      updatedFields[fieldIndex].isOpen = !updatedFields[fieldIndex].isOpen;
                                      setMappingFields(updatedFields);
                                    }}
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <p className="flex items-center gap-1 text-gray-800 text-[0.8vw]">
                                        {field.mappingField}
                                      </p>
                                      <div className="flex items-center">
                                        <span className={`duration-300 ${field.isOpen ? '-rotate-180' : 'rotate-0'}`}>
                                          <div className="border-[0.4vw] rounded-t-[0.2vw] border-transparent border-t-[#777] translate-y-[25%]" />
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              }
                            />
                            {validationErrors[field.attributeId] && (
                              <div className="relative">
                                <div className="absolute -top-[0.2vw] left-0 text-yellow-600 text-[0.7vw] px-[0.5vw] py-[0.2vw] rounded-sm">
                                  {validationErrors[field.attributeId]}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
                {/* Add Attribute Modal */}
                {activeAddAttributeScope === scope.id && (
                  <div className="mt-2">
                    <div className="grid grid-cols-2">
                      <div className="col-span-2">
                        <div className="flex items-center gap-4">
                          {/* Attribute name */}
                          <div className="flex-column w-[45%]">
                            <label className="block text-[.8vw] font-medium text-gray-700 mb-1 pl-1 w-[60%]">Attribute Name</label>
                            <Input
                              className="w-[75%] text-[0.8vw] rounded-lg"
                              value={newAttribute.name}
                              onChange={(e) => setNewAttribute({ ...newAttribute, name: e.target.value })}
                              placeholder="Enter attribute name"
                            />
                          </div>
                          
                          {/* Attribute  type*/}
                          <div className="w-[30%] pl-7">
                            <label className="block text-[.8vw] font-medium text-gray-700 mb-1 pl-1">Attribute Type</label>
                            <Dropdown
                              open={newAttribute.typeDropdownOpen}
                              setOpen={(isOpen) => {
                                setNewAttribute({ ...newAttribute, typeDropdownOpen: isOpen });
                              }}
                              data={attributeTypes.map(type => ({ id: type.name, label: type.code }))}
                              width="min-w-[100%]"
                              height="max-h-[32vh]"
                              className="w-[70%] text-[0.8vw] rounded-lg"
                              editFilter={(selectedId) => {
                                setNewAttribute({ ...newAttribute, type: selectedId, typeDropdownOpen: false });
                              }}
                              button={
                                <div className="w-full">
                                  <div
                                    className="flex justify-between items-center py-1 px-2 cursor-pointer transition-colors duration-100 border border-gray-200 rounded-lg"
                                    onClick={() => {
                                      setNewAttribute({ ...newAttribute, typeDropdownOpen: !newAttribute.typeDropdownOpen });
                                    }}
                                  >
                                    <p className="flex items-center gap-1 text-gray-800">
                                      {newAttribute.type ? attributeTypes.find(t => t.name === newAttribute.type)?.code || 'Select type' : 'Select type'}
                                    </p>
                                    <span className={`duration-300 ${newAttribute.typeDropdownOpen ? '-rotate-180' : 'rotate-0'}`}>
                                      <div className="border-[0.4vw] rounded-t-[0.2vw] border-transparent border-t-[#777] translate-y-[25%]" />
                                    </span>
                                  </div>
                                </div>
                              }
                            />
                          </div>
                          
                          {/* Create close and open */}
                          <div className="flex items-center space-x-2 mt-6">
                            <button
                              onClick={handleAddAttributeModalClose}
                              className="flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded-full transition-colors"
                            >
                              <Close
                                stroke="#616874"
                                circle={false}
                                width="1.5vw"
                                height="1.5vw"
                              />
                            </button>
                            <button
                              onClick={handleCreateAttribute}
                              className="flex items-center justify-center w-6 h-6"
                            >
                              <Tick width="0.8vw" height="0.8vw" strokeWidth="3.2" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Add Attribute Button */}
                <div className="flex text-[#616874] text-[.8vw] items-center font-[500] gap-[.4vw] cursor-pointer mt-1 w-20"
                  onClick={() => handleAddAttribute(scope.id, scope.label)}
                >
                  <AddCircle width="1vw" strokeWidth="2.5" stroke="#616874" />
                    <p>Add Attribute</p>
                  </div>
                <div className="border-b pt-2"></div>
              </div>
            ))}
            
            {/* Add Scope Form */}
            {showAddScope && (
              <div className="mt-4">
                <div className="grid grid-cols-2">
                  <div className="col-span-2">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-[.8vw] font-medium text-gray-700 mb-1">Scope Name</label>
                        <Input
                          className="w-[70%] text-[0.8vw] rounded-lg"
                          value={newScope.name}
                          onChange={(e) => setNewScope({ ...newScope, name: e.target.value })}
                          placeholder="Enter scope name"
                          autoFocus
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-6">
                        <button
                          onClick={handleAddScopeClose}
                          className="flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <Close
                              stroke="#616874"
                              circle={false}
                              width="2vw"
                              height="2vw"
                            />
                        </button>
                        <button
                          onClick={handleCreateScope}
                          className="flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <Tick width="1vw" height="1vw" strokeWidth="3.2" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Add Scope Button */}
              <div
                className="flex text-[black] text-[1vw] items-center font-semibold gap-[.4vw] cursor-pointer w-20 pt-2"
                onClick={handleAddScope}
              >
                <AddCircle width="1.2vw" strokeWidth="2.5" stroke="black" />
                <p>Add Scope</p>
              </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="col-span-1">
              <h3 className="text-gray-700 font-medium mb-4">CRM Fields</h3>
              {mappingFields.map((field, index) => (
                <div key={index} className="py-2 border-b border-gray-200">
                  <div className="py-1 px-2 border border-gray-200 rounded-lg">
                    <p className="text-gray-700">{field.crmField}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="col-span-1">
              <h3 className="text-gray-700 font-medium mb-4">Mapping field</h3>
              {mappingFields.map((field, index) => (
                <div key={index} className="py-3 border-b border-gray-200">
                  <Dropdown
                    open={field.isOpen}
                    setOpen={(isOpen) => {
                      const updatedFields = [...mappingFields];
                      updatedFields[index].isOpen = isOpen;
                      setMappingFields(updatedFields);
                    }}
                    data={[
                      ...fileColumns.map(column => ({ 
                        id: column, 
                        label: column,
                        selected: mappingFields.some(f => f.mappingField === column && f.crmField !== field.crmField)
                      })),
                      { id: 'ignore', label: 'Ignore' }
                    ]}
                    width="min-w-[100%]"
                    height="max-h-[32vh]"
                    showCheck={({id}) => {
                      // Show checkmark if this column is already mapped to another field
                      return mappingFields.some(f => f.mappingField === id && f.crmField !== field.crmField);
                    }}
                    editFilter={(selectedId) => {
                      // Check if this column is already mapped to another field
                      const isAlreadyMapped = mappingFields.some(
                        f => f.mappingField === selectedId && 
                        f.crmField !== field.crmField && 
                        selectedId !== 'ignore'
                      );
                      
                      // Only update if the column is not already mapped or if it's 'Ignore'
                      if (!isAlreadyMapped || selectedId === 'ignore') {
                        const updatedFields = [...mappingFields];
                        updatedFields[index].mappingField = selectedId === 'ignore' ? 'Ignore' : selectedId;
                        updatedFields[index].isOpen = false;
                        setMappingFields(updatedFields);
                      }
                    }}
                    button={
                      <div className="w-full">
                        <div
                          className="flex justify-between items-center py-1 px-2 cursor-pointer transition-colors duration-100 border border-gray-200 rounded-lg"
                          onClick={() => {
                            const updatedFields = [...mappingFields];
                            updatedFields[index].isOpen = !updatedFields[index].isOpen;
                            setMappingFields(updatedFields);
                          }}
                        >
                          <div className="flex items-center justify-between w-full">
                            <p className="flex items-center gap-1 text-gray-800">
                              {field.mappingField}
                            </p>
                            <div className="flex items-center">
                              <span className={`duration-300 ${field.isOpen ? '-rotate-180' : 'rotate-0'}`}>
                                <div className="border-[0.4vw] rounded-t-[0.2vw] border-transparent border-t-[#777] translate-y-[25%]" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Import Button */}
      <div className="sticky bottom-0 rounded-br-2xl">
        <hr />
        <div className="px-[1.2vw] py-[.5vw] flex-row justify-end align-end items-center gap-3">
          <Tooltip 
            title={!canImport ? "Both mobile number and country code must be mapped to import contacts" : null}
            placement="top"
            color="#ff4d4f"
            trigger="hover"
          >
            <button
              className={`px-4 py-1.5 ${canImport ? 'button-primary' : 'button-primary opacity-50 cursor-not-allowed'}`}
              onClick={canImport ? onImport : undefined}
              disabled={!canImport}
            >
              {continueLoader ? (
                <Loader Width={70} Height={18} loaderBg="white" />
              ) : (
                "Import"
              )}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ColumnMapping;
